import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle2, MessageCircle } from 'lucide-react';
import StorefrontFrame from '../storefront/frame/StorefrontFrame';
import SupportChatWidget from '../../support/SupportChatWidget';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// form fields on the theme's own line and radius (the frame's variables)
const fieldStyle = { borderColor: 'var(--s6-line)', borderRadius: 'var(--s6-radius-input)' };
const fieldCls = 'w-full border bg-white px-4 py-3 text-sm outline-none focus:border-[color:var(--s6-primary)]';

/**
 * StorefrontContactPage (v4.25, reframed v5.67) — the dedicated "Contact"
 * page linked from the storefront nav. It wears the v7 frame (the home
 * page's header, phone menu, footer, theme, fonts and fail-closed gate).
 *
 * The contact form files a ticket through the EXISTING support endpoint
 * (POST /api/support/tickets/{subdomain}) — the same rail SupportChatWidget
 * uses — so we never invent a new send path. A mailto fallback + a live
 * "chat with us" launcher (SupportChatWidget openChat) are also offered.
 */
const ContactBody = ({ brand: b, subdomain, storeSlug }) => {
  // Contact form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !EMAIL_RE.test(email.trim()) || !message.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const storeParam = storeSlug ? `?store_id=${encodeURIComponent(storeSlug)}` : '';
      const res = await fetch(`${base}/api/support/tickets/${subdomain}${storeParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: name.trim(),
          customer_email: email.trim(),
          subject: `Contact form — ${name.trim()}`,
          initial_message: message.trim(),
          source: storeSlug ? 'store_contact_page' : 'contact_page',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch (_e) {
      setError("Sorry — that didn't send. Try again, or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const brandName = b.brand_name || 'Our Kitchen';
  const hasSocial = b.instagram_url || b.facebook_url || b.website_url;

  // A Google-maps embed src that reliably renders: query by street address when
  // available, else by the share-link text. "Open in Maps" always links out to
  // the merchant's actual google_maps_url.
  const mapQuery = b.contact_address || b.google_maps_url || '';
  const mapEmbedSrc = mapQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`
    : null;

  const iconStyle = { color: 'var(--s6-primary)' };
  const linkStyle = { color: 'var(--s6-primary-text)' };
  const mutedStyle = { color: 'var(--s6-ink-muted)' };

  return (
    <div data-testid="storefront-contact-page">
      <section className="s6-section" data-testid="contact-body">
        <div className="s6-container">
          <div className="s6-head s6-head-left">
            <span className="s6-eyebrow">Say hello</span>
            <h1 className="s6-h2">Get in touch</h1>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Left — contact details + map */}
            <div className="space-y-6">
              {b.contact_address && (
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 shrink-0 mt-0.5" style={iconStyle} />
                  <div>
                    <div className="text-sm font-semibold mb-0.5">Visit</div>
                    <div className="text-sm whitespace-pre-line" style={mutedStyle}>{b.contact_address}</div>
                    {b.google_maps_url && (
                      <a href={b.google_maps_url} target="_blank" rel="noreferrer" className="text-sm underline underline-offset-4 mt-1 inline-block" style={linkStyle}>
                        Open in Google Maps →
                      </a>
                    )}
                  </div>
                </div>
              )}
              {b.contact_hours && (
                <div className="flex gap-3">
                  <Clock className="w-5 h-5 shrink-0 mt-0.5" style={iconStyle} />
                  <div>
                    <div className="text-sm font-semibold mb-0.5">Hours</div>
                    <div className="text-sm whitespace-pre-line" style={mutedStyle}>{b.contact_hours}</div>
                  </div>
                </div>
              )}
              {b.contact_phone && (
                <div className="flex gap-3">
                  <Phone className="w-5 h-5 shrink-0 mt-0.5" style={iconStyle} />
                  <div>
                    <div className="text-sm font-semibold mb-0.5">Call</div>
                    <a href={`tel:${b.contact_phone}`} className="text-sm hover:underline" style={mutedStyle}>{b.contact_phone}</a>
                  </div>
                </div>
              )}
              {b.contact_email && (
                <div className="flex gap-3">
                  <Mail className="w-5 h-5 shrink-0 mt-0.5" style={iconStyle} />
                  <div>
                    <div className="text-sm font-semibold mb-0.5">Email</div>
                    <a href={`mailto:${b.contact_email}`} className="text-sm hover:underline" style={mutedStyle}>{b.contact_email}</a>
                  </div>
                </div>
              )}

              {hasSocial && (
                <div className="flex gap-3">
                  <MessageCircle className="w-5 h-5 shrink-0 mt-0.5" style={iconStyle} />
                  <div>
                    <div className="text-sm font-semibold mb-1">Follow</div>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {b.website_url && <a href={b.website_url} target="_blank" rel="noreferrer" className="underline underline-offset-4" style={linkStyle}>Website</a>}
                      {b.instagram_url && <a href={b.instagram_url} target="_blank" rel="noreferrer" className="underline underline-offset-4" style={linkStyle}>Instagram</a>}
                      {b.facebook_url && <a href={b.facebook_url} target="_blank" rel="noreferrer" className="underline underline-offset-4" style={linkStyle}>Facebook</a>}
                    </div>
                  </div>
                </div>
              )}

              {mapEmbedSrc && (
                <iframe
                  title={`${brandName} location`}
                  src={mapEmbedSrc}
                  className="w-full h-64 border"
                  style={{ borderColor: 'var(--s6-line)', borderRadius: 'var(--s6-radius-photo)' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              )}
            </div>

            {/* Right — contact form (files a support ticket) */}
            <div>
              {sent ? (
                <div className="s6-card p-8 text-center" data-testid="contact-form-success">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={iconStyle} />
                  <h2 className="s6-h3 mb-1">Message sent</h2>
                  <p className="text-sm" style={mutedStyle}>
                    Thanks for reaching out — the {brandName} team will get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="s6-card p-6 space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="contact-name">Name</label>
                    <input
                      id="contact-name"
                      className={fieldCls}
                      style={fieldStyle}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="contact-email">Email</label>
                    <input
                      id="contact-email"
                      type="email"
                      className={fieldCls}
                      style={fieldStyle}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message"
                      className={`${fieldCls} h-32 resize-none`}
                      style={fieldStyle}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="How can we help?"
                      data-testid="contact-message"
                    />
                  </div>
                  {error && <p className="text-sm" style={{ color: 'var(--s6-closed)' }} data-testid="contact-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting || !name.trim() || !EMAIL_RE.test(email.trim()) || !message.trim()}
                    className="s6-btn s6-btn-block"
                    data-testid="contact-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? 'Sending…' : 'Send message'}
                  </button>
                  {b.contact_email && (
                    <p className="text-xs text-center" style={mutedStyle}>
                      Prefer email? Write to{' '}
                      <a href={`mailto:${b.contact_email}`} className="underline underline-offset-4" style={linkStyle}>
                        {b.contact_email}
                      </a>
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Live "chat with us" launcher — the existing support widget, no new
          send path invented. Anonymous open-chat (AI + human handoff). */}
      <SupportChatWidget
        subdomain={subdomain}
        storeId={storeSlug || null}
        mode="openChat"
        agentName={b.opsy_display_name || 'OpSy'}
      />
    </div>
  );
};

const StorefrontContactPage = () => (
  <StorefrontFrame pageType="contact">
    {(ctx) => <ContactBody {...ctx} />}
  </StorefrontFrame>
);

export default StorefrontContactPage;
